import type { ReactNode } from 'react';
import { useAuthState } from '../lib/auth-context';

type Variant = 'info' | 'warning' | 'error';

function Banner({
  variant,
  title,
  message,
  children,
}: {
  variant: Variant;
  title: string;
  message: string;
  children?: ReactNode;
}) {
  const colors: Record<Variant, string> = {
    info: '#1c7ed6',
    warning: '#f08c00',
    error: '#ff6b6b',
  };
  const background: Record<Variant, string> = {
    info: '#e7f5ff',
    warning: '#fff4e6',
    error: '#ffe3e3',
  };

  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        background: background[variant],
        color: '#1f2328',
        border: `1px solid ${colors[variant]}`,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: colors[variant] }}>
        {title}
      </div>
      <div style={{ fontSize: '0.95rem', lineHeight: 1.4 }}>{message}</div>
      {children && <div style={{ marginTop: '0.75rem' }}>{children}</div>}
    </div>
  );
}

export function AuthStatusBanner() {
  const auth = useAuthState();

  if (auth.status === 'authenticated') {
    return null;
  }

  if (auth.status === 'cached-session') {
    return (
      <Banner
        variant="info"
        title="Используем сохранённую сессию"
        message="Последняя успешная авторизация активна. Данные отображаются в режиме чтения."
      />
    );
  }

  if (auth.status === 'read-only' && auth.session?.source === 'read-only') {
    return (
      <Banner
        variant="info"
        title="Режим просмотра"
        message="Приложение запущено без initData (например, через меню Telegram). Доступна только загрузка данных."
      />
    );
  }

  if (auth.status === 'needs-initdata') {
    return (
      <Banner
        variant="warning"
        title="Требуется запуск через кнопку"
        message="Чтобы загрузить ваши данные, откройте мини-приложение через кнопку в чате с ботом. Мы не получили initData от Telegram."
      >
        <button onClick={auth.retry}>Проверить снова</button>
      </Banner>
    );
  }

  if (auth.status === 'read-only') {
    return (
      <Banner
        variant="info"
        title="Режим просмотра"
        message="Вы просматриваете приложение вне Telegram. Для доступа к личным данным откройте мини-приложение внутри Telegram."
      />
    );
  }

  if (auth.status === 'error') {
    return (
      <Banner
        variant="error"
        title="Ошибка авторизации"
        message={auth.message || 'Не удалось связаться с сервером. Попробуйте позже.'}
      >
        <button onClick={auth.retry}>Повторить</button>
      </Banner>
    );
  }

  return null;
}

